import { exists } from 'helpers/exists'
import { RefObject, useCallback, useEffect, useState } from 'react'

export const useFocusInside = (
  focusContainers: RefObject<HTMLElement | null>[],
  supportKeyEvents?: boolean,
) => {
  const [focused, setFocused] = useState(false)

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        setFocused(false)
      }
    },
    [focusContainers, focused],
  )

  useEffect(() => {
    if (supportKeyEvents) {
      document.addEventListener('keydown', onKeyDown)

      return () => {
        document.removeEventListener('keydown', onKeyDown)
      }
    }
  }, [onKeyDown, supportKeyEvents])

  const onBlur = useCallback(() => {
    const elements = focusContainers.map(ref => ref.current).filter(exists)
    setTimeout(() => {
      const focusInside = elements.some(
        e =>
          e === window.document.activeElement ||
          e.contains(window.document.activeElement),
      )
      if (focusInside !== focused) {
        setFocused(focusInside)
      }
    })
  }, [focusContainers, focused])

  const onFocusCapture = useCallback(() => {
    if (!focused) {
      setFocused(true)
    }
  }, [focused])

  return {
    focused,
    onBlur,
    onFocusCapture,
  }
}
